import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  InputLabel,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipButton } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { quoteClip } from "data/mutations-common";
import {
  fetchClipSummary,
  fetchCredits,
  fetchSubscription,
} from "data/queries-common";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router";
import { useIntercom } from "react-use-intercom";
import * as Routes from "routes";
import { toTitleCase } from "utils/commons";
import { openDownloadLinkCallback } from "utils/download";
import { resolveIdentityId } from "utils/identity";
import {
  ClipConfigProps,
  ClipStepsControlProps,
  hasPremiumData,
} from "../../clip-menu";
import { ClipConfiguration } from "../../models";
import { PurchaseWithCredits } from "./purchase-with-credits";
import { PurchaseWithDollars } from "./purchase-with-dollars";
import { SummaryTable } from "./summary-table";

export const ClipSummary = (props: ClipConfigProps & ClipStepsControlProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const history = useHistory();

  const { enqueueQueryFailed, enqueueMutationFailed } = useCustomSnackbars();
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  // Check if clip has premium data to determine which permission to check
  const clipHasPremiumDataForDownload = hasPremiumData(props.clipConfig);

  const clipSummaryQuery = useQuery({
    queryKey: [QueryKeys.clipSummaryKey, identityId],
    queryFn: () => fetchClipSummary(props.clipConfig.id),
  });

  useEffect(() => {
    if (clipSummaryQuery.isError) {
      enqueueQueryFailed(clipSummaryQuery.error.toString());
    }
  }, [clipSummaryQuery.isError]);

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  useEffect(() => {
    if (subscriptionQuery.isError) {
      enqueueQueryFailed(subscriptionQuery.error.toString());
    }
  }, [subscriptionQuery.isError]);

  // PRELOAD CREDITS QUERY
  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
  });

  const [subStep, setSubStep] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const isSummaryOnly = props.clipConfig.status !== "Quoted";
  const timeDelta = dayjs().utc().unix() - (props.clipConfig.quotedOn ?? 0);
  const isExpired = timeDelta > 5 * 24 * 60 * 60;
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const onQuoteSubmit = async () => {
    const newClip = {
      ...props.clipConfig,
      activeStep: props.activeStep - 1,
    };
    props.setClipConfig(newClip);
    props.saveClipConfig(newClip);
    clipsQuoteMutation.mutate(props.clipConfig.id);
  };

  const clipsQuoteMutation = useMutation({
    mutationFn: (clipId: string) => quoteClip(clipId),
    onSuccess: (clipConfig: ClipConfiguration) => {
      props.setClipConfig((prev: ClipConfiguration) => {
        return {
          ...prev,
          status: clipConfig.status,
        };
      });

      (queryClient.invalidateQueries({
        queryKey: [QueryKeys.clipsKey, identityId],
      }),
        enqueueSnackbar("Clip has been submitted for quoting.", {
          variant: "success",
        }));
      props.resetClipConfig();
    },
    onError: (error: Error) => {
      enqueueMutationFailed(error.toString());
    },
  });

  const onEdit = () => {
    const newClip: ClipConfiguration = {
      ...props.clipConfig,
      activeStep: 1,
      status: "Draft",
    };

    props.setClipConfig(newClip);
    props.saveClipConfig(newClip);
  };
  const { show } = useIntercom();

  const hideForExpired = props.clipConfig.status === "Quoted" && isExpired;

  const clipTooBig =
    clipSummaryQuery.isSuccess &&
    subscriptionQuery.isSuccess &&
    subscriptionQuery.data.activeSubscription &&
    clipSummaryQuery.data.dollarCost > 10000;

  const isCashPaymentDisabled = !isPermitted([PermissionsKeys.clipPayDollars]);
  const canPurchaseOversizedClip = isPermitted([PermissionsKeys.clipOversized]);

  // Check if clip contains premium data and if user has permission to submit it
  const clipHasPremiumData = hasPremiumData(props.clipConfig);
  const hasPremiumDataPermission = isPermitted([
    PermissionsKeys.premiumDataCreate,
  ]);
  const isPremiumDataBlocked = clipHasPremiumData && !hasPremiumDataPermission;
  const premiumDataTooltip = isPremiumDataBlocked
    ? permissionDeniedMessageString([PermissionsKeys.premiumDataCreate])
    : "";

  return (
    <>
      {subStep == 0 && (
        <>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            maxHeight="calc(82vh - 64px)"
            overflow="auto"
          >
            <Typography variant="h5">{`${
              props.clipConfig.status === "Quoted" ? "Quote" : "Order"
            } Summary`}</Typography>
            {props.clipConfig.status === "Quoted" && !isExpired && (
              <Alert severity="info" variant="outlined">
                Quote is valid for 5 days
              </Alert>
            )}
            {props.clipConfig.status === "Quoted" && isExpired && (
              <Alert severity="error" variant="outlined">
                <AlertTitle>Quote has expired</AlertTitle>
                Please click 'Requote' button to submit this again.
              </Alert>
            )}
            {props.clipConfig.status === "Processing" && (
              <Alert severity="info" variant="outlined">
                Clip Processing, you will be notified when it's ready
              </Alert>
            )}
            <Box minHeight="43px">
              <InputLabel>Name</InputLabel>
              <Typography noWrap variant="body2">
                {props.clipConfig.name}
              </Typography>
            </Box>
            <Box minHeight="43px">
              <InputLabel>Format</InputLabel>
              <Typography variant="body2">{props.clipConfig.format}</Typography>
            </Box>
            <Box minHeight="43px">
              <InputLabel>Coordinate Reference System</InputLabel>
              <Typography variant="body2">{props.clipConfig.crs}</Typography>
            </Box>
            {props.clipConfig.clientReference && (
              <Box minHeight="43px">
                <InputLabel>Client Reference</InputLabel>
                <Typography variant="body2">
                  {props.clipConfig.clientReference}
                </Typography>
              </Box>
            )}
            {props.clipConfig.notes && (
              <Box minHeight="43px">
                <InputLabel>Notes</InputLabel>
                <Typography variant="body2">
                  {props.clipConfig.notes}
                </Typography>
              </Box>
            )}
            <Typography variant="subtitle1">Data</Typography>

            {clipSummaryQuery.isSuccess && subscriptionQuery.isSuccess && (
              <>
                <SummaryTable
                  quoteSummary={clipSummaryQuery.data}
                  showCredits={
                    !hideForExpired &&
                    !(clipTooBig && !canPurchaseOversizedClip)
                  }
                  showFeatures={!hideForExpired}
                  showRate={
                    !hideForExpired &&
                    !(clipTooBig && !canPurchaseOversizedClip)
                  }
                  showTotal={!hideForExpired}
                ></SummaryTable>
                {!isExpired &&
                  clipSummaryQuery.data.totalFeatures > 0 &&
                  (!clipTooBig || canPurchaseOversizedClip) && (
                    <>
                      <Typography variant="h5">Quote</Typography>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {`Credits due: ${clipSummaryQuery.data.totalCredits.toLocaleString()}`}
                      </Typography>
                      {!isCashPaymentDisabled &&
                        subscriptionQuery.data.activeSubscription && (
                          <Typography variant="subtitle2" fontWeight="bold">
                            {`Dollar due: $${clipSummaryQuery.data.dollarCost.toLocaleString(
                              "en-AU",
                              {
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                              },
                            )} inc GST`}
                          </Typography>
                        )}
                    </>
                  )}
                {clipSummaryQuery.data.totalFeatures === 0 && (
                  <Alert severity="warning">
                    The quoted area contains no features. Return to edit mode to
                    modify the clipped area.
                  </Alert>
                )}
                {clipTooBig && !canPurchaseOversizedClip && (
                  <Alert
                    icon={<InfoOutlinedIcon />}
                    color="info"
                    variant="outlined"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => show()}
                      >
                        Contact
                      </Button>
                    }
                  >
                    <AlertTitle>Quote too large</AlertTitle>
                    Talk to us about a commercial license, it is significantly
                    better value.
                  </Alert>
                )}
                {isPremiumDataBlocked && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() =>
                          history.push(Routes.orgManageSubscription)
                        }
                      >
                        Upgrade
                      </Button>
                    }
                  >
                    <AlertTitle>Upgrade required to purchase</AlertTitle>
                    This clip includes premium data. To complete your purchase,
                    please upgrade to the Team plan.
                  </Alert>
                )}
              </>
            )}
            {!isSummaryOnly &&
              (clipSummaryQuery.isPending || subscriptionQuery.isPending) && (
                <Skeleton variant="rectangular" height={100}></Skeleton>
              )}
            {((!isSummaryOnly && clipSummaryQuery.isError) ||
              subscriptionQuery.isError) && (
              <Alert severity="error" variant="outlined">
                Unable to fetch quote summary
              </Alert>
            )}
          </Box>
          <Box
            alignSelf="flex-end"
            marginTop="auto"
            display="flex"
            paddingTop={1}
            gap={1}
          >
            {!isSummaryOnly &&
              !isExpired &&
              clipSummaryQuery.isSuccess &&
              subscriptionQuery.isSuccess && (
                <>
                  <Button
                    color="secondary"
                    variant="text"
                    onClick={() => setEditModalOpen(true)}
                  >
                    Edit
                  </Button>
                  <ConfirmationModal
                    isLoading={false}
                    isError={false}
                    open={editModalOpen}
                    header="Confirm Clip Modification"
                    body="Editing this clip will cancel the current quote. A new quoting process will begin once you resubmit the clip"
                    leftButtonText={"Cancel"}
                    leftButtonColor="primary"
                    handleLeftButton={() => setEditModalOpen(false)}
                    rightButtonText={"Edit Clip"}
                    rightButtonColor="warning"
                    handleRightButton={onEdit}
                    handleClose={() => setEditModalOpen(false)}
                  />
                  <Tooltip title={premiumDataTooltip}>
                    <span>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setSubStep(2);
                        }}
                        disabled={
                          subscriptionQuery.isPending ||
                          isCashPaymentDisabled ||
                          clipSummaryQuery.isPending ||
                          clipSummaryQuery?.data?.totalFeatures === 0 ||
                          clipTooBig ||
                          !subscriptionQuery.data.activeSubscription ||
                          isPremiumDataBlocked
                        }
                      >
                        Pay $
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={premiumDataTooltip}>
                    <span>
                      <Button
                        variant="contained"
                        disabled={
                          clipSummaryQuery.isPending ||
                          clipSummaryQuery?.data?.totalFeatures === 0 ||
                          (!canPurchaseOversizedClip && clipTooBig) ||
                          !subscriptionQuery.data.activeSubscription ||
                          isPremiumDataBlocked
                        }
                        onClick={() => setSubStep(1)}
                      >
                        Pay Credits
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            {props.clipConfig.status === "Quoted" && isExpired && (
              <LoadingButton
                loading={clipsQuoteMutation.isPending}
                disabled={clipsQuoteMutation.isPending}
                onClick={async () => {
                  await onQuoteSubmit();
                }}
              >
                Requote
              </LoadingButton>
            )}
          </Box>
        </>
      )}
      {subStep == 1 &&
        clipSummaryQuery.isSuccess &&
        subscriptionQuery.isSuccess &&
        subscriptionQuery.data.activeSubscription && (
          <PurchaseWithCredits
            setClipConfig={props.setClipConfig}
            resetClipConfig={props.resetClipConfig}
            clipConfig={props.clipConfig}
            quoteSummary={clipSummaryQuery.data}
            planName={toTitleCase(
              subscriptionQuery.data.activeSubscription.plan
                .product_display_name,
            )}
            setSubStep={setSubStep}
          />
        )}
      {subStep == 2 &&
        clipSummaryQuery.isSuccess &&
        subscriptionQuery.isSuccess &&
        subscriptionQuery.data.activeSubscription && (
          <PurchaseWithDollars
            clipConfig={props.clipConfig}
            quoteSummary={clipSummaryQuery.data}
            plan={subscriptionQuery.data.activeSubscription.plan}
            resetClipConfig={props.resetClipConfig}
            setSubStep={setSubStep}
          />
        )}
      {(props.clipConfig.status === "Processing" ||
        props.clipConfig.status === "Completed") && (
        <Box
          alignSelf="flex-end"
          marginTop="auto"
          paddingTop={1}
          display="flex"
        >
          <Button
            disabled={!props.clipConfig.invoiceUrl}
            onClick={() => {
              window.open(props.clipConfig.invoiceUrl, "_blank");
            }}
          >
            Download Invoice
          </Button>
          {clipHasPremiumDataForDownload ? (
            <PermissionTooltipButton
              permissionKeys={[PermissionsKeys.premiumDataRead]}
              additionalDisabled={props.clipConfig.status !== "Completed"}
              onClick={openDownloadLinkCallback(
                `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                  props.clipConfig.id
                }/output`,
              )}
            >
              Download
            </PermissionTooltipButton>
          ) : (
            <Button
              disabled={props.clipConfig.status !== "Completed"}
              variant="text"
              onClick={openDownloadLinkCallback(
                `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                  props.clipConfig.id
                }/output`,
              )}
            >
              Download
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
